/**
 * @file 订单轮询 hook
 * @description 基于 react-query 实现订单状态轮询，自动处理竞态、abort 和停止条件
 */

import {useQuery} from '@tanstack/react-query';
import {useEffect, useRef} from 'react';
import {queryOrder} from '@/services/commerce/payment';
import type {OrderPollingResult} from '@/services/commerce/entities/payment';
import {OrderStatusValue} from '@/services/commerce/entities/payment';
import type {OrderId} from '@/services/commerce/type';

/**
 * 订单轮询 hook
 * - queryKey 带 orderId，切换/卸载时 react-query 自动 abort + 停止轮询
 * - PENDING 态 3s 轮询，PAID 态 500ms 快速轮询（等权益下发），其他状态停止
 * - 请求失败或首次未返回时保持 3s 间隔重试，不静默停止
 */
export function useOrderPolling(
    orderId: OrderId | null,
    onStatus: (s: OrderPollingResult) => void,
) {
    // 用 ref 持有最新回调，避免每次 render 都触发 useEffect 重执行
    const onStatusRef = useRef(onStatus);
    onStatusRef.current = onStatus;

    const {data} = useQuery({
        queryKey: ['payment.order', orderId],
        // signal 由 react-query 提供，orderId 变化或组件卸载时自动 abort
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        queryFn: ({signal}) => queryOrder(orderId!, {signal}),
        enabled: !!orderId,
        // 根据订单状态动态调整轮询间隔
        refetchInterval: ({state}) => {
            const status = state.data?.orderStatus;
            if (!status) return 3000; // 请求失败或首次未返回，保持默认间隔重试
            if (status === OrderStatusValue.PENDING) return 3000; // 待支付：3s 轮询
            if (status === OrderStatusValue.PAID) return 500; // 已支付等权益下发：500ms 快速轮询
            return false; // 其他状态（RIGHTS_ISSUED/FAILED/REFUNDED）：停止轮询
        },
        refetchIntervalInBackground: false, // 用户切走 tab 时暂停轮询
        staleTime: 0,
        gcTime: 0, // 卸载/切换后立即丢弃缓存，避免下次挂载读到旧数据
        retry: false, // 支付轮询不做自动重试，失败态由业务显式处理
    });

    // 把 react-query 的结果桥接到 reducer
    useEffect(() => {
        if (data) onStatusRef.current(data);
    }, [data]);
}
